import { useEffect, useState } from 'react';
import clsx from 'clsx';

import { TaskField } from '@atom/types/task';
import { toggleFromSet } from '@atom/utilities/setUtilities';
import {
  CustomFieldView,
  getTaskFieldWidth,
} from '@atom/utilities/taskUtilities';
import { getRequiredMultiSelectTitle } from '@atom/utilities/workOrderFieldUtilities';

import classes from './taskFields.module.css';

type TaskEnumMultipleFieldListProps = {
  field: TaskField;
  onChange: (fieldId: string, value: any) => void;
  isDisabled?: boolean;
  error?: boolean;
  helperText?: string;
  onClick?: () => void;
  taskId?: string;
  customFieldView: CustomFieldView;
  dataCy?: string;
};

const TaskEnumMultipleFieldList = ({
  field,
  onChange,
  isDisabled,
  error,
  helperText,
  onClick,
  customFieldView,
  dataCy,
}: TaskEnumMultipleFieldListProps) => {
  const [values, setValues] = useState<Set<string>>(new Set([]));
  const baseDataCy = dataCy ?? `taskField-${field.title}-EnumMultipleList`;

  useEffect(() => {
    setValues(field?.value ? new Set(field.value) : new Set([]));
  }, [field?.value]);

  const title =
    field.required && customFieldView !== CustomFieldView.FILTER
      ? getRequiredMultiSelectTitle(field.title, field.selectAllOnCompleteWork)
      : field.title;

  const containerWidth = {
    maxWidth: getTaskFieldWidth(customFieldView),
  };

  const toggleCheck = (option: string) => {
    if (isDisabled) {
      return;
    }
    const newValues = toggleFromSet(values, option);
    setValues(newValues);
    onChange(field.id, [...newValues]);
  };

  return (
    <div
      onClick={onClick}
      className={classes.fieldContainer}
      style={containerWidth}
      data-cy={baseDataCy}
    >
      <div
        className={clsx(classes.fieldTitle, error && classes.fieldTitleError)}
      >
        {title}
      </div>
      <div className={classes.listContainer}>
        {field.enumeration.map(option => (
          <div key={option} className={classes.listItem}>
            <div className={classes.listInputContainer}>
              <input
                className={classes.listInput}
                type="checkbox"
                onClick={() => toggleCheck(option)}
                checked={values.has(option)}
                disabled={isDisabled}
                data-cy={`${baseDataCy}-option-${option}`}
              />
            </div>
            <div onClick={() => toggleCheck(option)}>{option}</div>
          </div>
        ))}
      </div>
      {error && helperText && (
        <div className={classes.listHelperText}>{helperText}</div>
      )}
    </div>
  );
};

export default TaskEnumMultipleFieldList;
